import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { TrendingUp, Filter } from 'lucide-react'
import { seoAnalytics } from '@/lib/api'

interface TrafficChartProps {
  days: number
  section?: string
}

const SOURCE_COLORS: Record<string, string> = {
  google_organic: '#10b981',
  bing_organic: '#3b82f6',
  yahoo_organic: '#8b5cf6',
  duckduckgo_organic: '#f97316',
  google_ads: '#eab308',
  direct: '#64748b',
  referral: '#ec4899',
  other: '#94a3b8',
}

const SOURCE_LABELS: Record<string, string> = {
  google_organic: 'Google Organico',
  bing_organic: 'Bing',
  yahoo_organic: 'Yahoo',
  duckduckgo_organic: 'DuckDuckGo',
  google_ads: 'Google Ads',
  direct: 'Directo',
  referral: 'Referencias',
  other: 'Otros',
}

export default function TrafficChart({ days, section }: TrafficChartProps) {
  const [sourceFilter, setSourceFilter] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['seo-traffic-by-source', days, sourceFilter, section],
    queryFn: () => seoAnalytics.getTrafficBySource({
      days,
      source: sourceFilter || undefined,
      section: section || undefined,
    }),
  })

  // Get unique sources for filter
  const overviewQuery = useQuery({
    queryKey: ['seo-traffic-overview', days, section],
    queryFn: () => seoAnalytics.getTrafficOverview(days, section),
  })

  const sources = overviewQuery.data?.data?.by_source?.map((s: any) => s.source) || []

  // Transform data for chart - group by date and source (using unique visitors)
  const chartData = (() => {
    if (!data?.data || !Array.isArray(data.data)) return []

    // Group by date - use unique_ips (unique visitors) instead of visits (HTTP requests)
    const byDate: Record<string, Record<string, number>> = {}
    data.data.forEach((item: any) => {
      if (!byDate[item.date]) {
        byDate[item.date] = {}
      }
      const source = item.source || 'other'
      // Use unique_ips for more accurate visitor count
      byDate[item.date][source] = (byDate[item.date][source] || 0) + (item.unique_ips || 0)
    })

    // Convert to array format for Recharts
    return Object.entries(byDate)
      .map(([date, sources]) => ({
        date,
        dateFormatted: new Date(date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
        ...sources,
      }))
      .sort((a, b) => a.date.localeCompare(b.date))
  })()

  // Get all sources present in data
  const presentSources = chartData.length > 0
    ? [...new Set(chartData.flatMap(d => Object.keys(d).filter(k => k !== 'date' && k !== 'dateFormatted')))]
    : []

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30">
            <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Trafico por Fuente</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Visitantes unicos diarios por origen
            </p>
          </div>
        </div>

        {/* Source filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={sourceFilter || ''}
            onChange={(e) => setSourceFilter(e.target.value || null)}
            className="input input-sm w-40"
          >
            <option value="">Todas las fuentes</option>
            {sources.map((source: string) => (
              <option key={source} value={source}>
                {SOURCE_LABELS[source] || source}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card-body">
        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-pulse text-slate-400">Cargando datos...</div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-80 flex flex-col items-center justify-center text-slate-400">
            <TrendingUp className="w-12 h-12 mb-4 opacity-50" />
            <p>No hay datos de trafico para el periodo seleccionado</p>
            <p className="text-sm mt-1">Ejecuta una sincronizacion para obtener datos</p>
          </div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  {presentSources.map((source) => (
                    <linearGradient key={source} id={`gradient-traffic-${source}`} x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={SOURCE_COLORS[source] || '#94a3b8'}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={SOURCE_COLORS[source] || '#94a3b8'}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="dateFormatted"
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                  formatter={(value, name) => [
                    `${Number(value).toLocaleString()} visitantes`,
                    SOURCE_LABELS[name as string] || name
                  ]}
                />
                <Legend
                  verticalAlign="top"
                  height={36}
                  formatter={(value: string) => SOURCE_LABELS[value] || value}
                />
                {presentSources.map((source) => (
                  <Area
                    key={source}
                    type="monotone"
                    dataKey={source}
                    stroke={SOURCE_COLORS[source] || '#94a3b8'}
                    strokeWidth={2}
                    fill={`url(#gradient-traffic-${source})`}
                    stackId="1"
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  )
}
